<script setup lang="ts">
import { CSSProperties, onUnmounted, ref, watchEffect } from "vue";
import {
  SupportedEaseNameToCubicBezier,
  transitionStart$,
  transitionEnd$
} from "@zyzgroup/core-web";

const CSSTransitionNames = ["superRainbow"] as const;
export type CSSTransitionName = (typeof CSSTransitionNames)[number];

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

export type CSSTransitionOptions = {
  name?: CSSTransitionName;
  timingFunction?: SupportedEaseNameToCubicBezier;
  duration?: number;
  delay?: number;
};

const props = withDefaults(
  defineProps<{
    name?: CSSTransitionName;
    timingFunction?: SupportedEaseNameToCubicBezier;
    // linear
    // ease
    // ease-in
    // ease-in-out
    // ease-out
    // cubic-bezier(0.25, 0.1, 0.25, 1)
    duration?: number;
    delay?: number;
  }>(),
  {
    name: "superRainbow",
    duration: 300,
    timingFunction: "ease-out",
    delay: 0
  }
);

const emits = defineEmits<{
  (event: "csstransitionstart", e: TransitionEvent): void;
  (event: "csstransitionend", e: TransitionEvent): void;
}>();

const r = ref();

let s1;
let s2;
watchEffect(() => {
  if (!r.value || s1 || s2) {
    return;
  }
  s1 = transitionStart$(r.value).subscribe((e) => {
    emits("csstransitionstart", e);
  });
  s2 = transitionEnd$(r.value).subscribe((e) => {
    emits("csstransitionend", e);
  });
});

// 设置预定义变化
function getPredefinedStyle(options?: CSSTransitionOptions) {
  switch (options?.name || props.name) {
    case "superRainbow":
      return {
        common: {
          backgroundSize: "200% 100%",
          backgroundImage: `linear-gradient(90deg,red,orange,yellow,green,blue,violet)`
        },
        start: {
          backgroundPosition: "0 0"
        },
        end: {
          backgroundPosition: "-100% 100%"
        }
      };
    default:
      return {
        common: {} as CSSProperties,
        start: {} as CSSProperties,
        end: {} as CSSProperties
      };
  }
}

function getBasicStyle(options?: CSSTransitionOptions) {
  return {
    // css-transition 渐变属性 : 某个属性 从 指定的开始状态 过渡到 特定的结束状态
    // 一次性 + 不可暂停 + 不可反转

    // transition: 属性名称 duration timing-function delay

    // 可选属性名称:
    // all
    // width
    // height
    // border-width
    // color
    // background-color
    // border-color
    // text-decoration-color
    // fill
    // stroke
    // opacity
    // box-shadow
    // transform
    // filter
    // backdrop-filter

    // transition: `transform ${props.duration}ms ease-in-out, box-shadow ${props.duration}ms ease-in-out`,
    transition: `all ${
      typeof options?.duration == "number" ? options?.duration : props.duration
    }ms ${options?.timingFunction || props.timingFunction} ${
      typeof options?.delay == "number" ? options?.delay : props.delay
    }ms`
    // willChange: "box-shadow, transform",
  } as CSSProperties;
}

const style = ref<CSSProperties>({});

// 清除
function clear(options?: CSSTransitionOptions) {
  const predefinedStyle = getPredefinedStyle(options);
  style.value = {
    ...getBasicStyle(options),
    ...predefinedStyle.common,
    ...predefinedStyle.start
  };
}
// 初始化执行
clear();

// 触发
function trigger(options?: CSSTransitionOptions) {
  const predefinedStyle = getPredefinedStyle(options);
  style.value = {
    ...getBasicStyle(options),
    ...predefinedStyle.common,
    ...predefinedStyle.end
  };
}

onUnmounted(() => {
  s1?.unsubscribe();
  s2?.unsubscribe();
});

defineExpose({ clear, trigger });
</script>

<template>
  <div
    ref="r"
    class="css-transition"
    :style="style"
  >
    <slot></slot>
  </div>
</template>
